/**
 * useClickOutside hook
 * Detect clicks outside a referenced element
 * Useful for closing dropdowns, modals, and popovers
 */

import { useEffect, RefObject } from 'react';

/**
 * Hook to detect clicks outside a referenced element
 * Calls the handler function when a click occurs outside the element
 *
 * @param ref - React ref to the element to monitor
 * @param handler - Callback function to execute on outside click
 *
 * @example
 * ```typescript
 * const dropdownRef = useRef<HTMLDivElement>(null);
 * useClickOutside(dropdownRef, () => setIsOpen(false));
 * ```
 */
export function useClickOutside(
  ref: RefObject<HTMLElement>,
  handler: () => void
): void {
  useEffect(() => {
    /**
     * Handle click events on the document
     * Check if the click target is outside the referenced element
     */
    function handleClickOutside(event: MouseEvent): void {
      // If ref exists and click is outside the element, call handler
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    }

    // Bind the event listener to document
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup: Remove event listener on unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handler]); // Re-run effect if ref or handler changes
}
